"use client";

import { type PropsWithChildren, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/util";

interface StarsProps extends PropsWithChildren {
  animation?: boolean;
  className?: string;
}

export const Stars = ({ children, className, animation }: StarsProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = container.clientWidth;
    let height = container.clientHeight;
    canvas.width = width;
    canvas.height = height;

    const particles: {
      x: number;
      y: number;
      r: number;
      dx: number;
      dy: number;
      opacity: number;
    }[] = [];

    const init = () => {
      particles.length = 0;
      for (let i = 0; i < 120; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 2.2,
          dx: (Math.random() - 0.5) * 0.6,
          dy: (Math.random() - 0.5) * 0.6,
          opacity: Math.random() * 0.01 + 0.3,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, width, height);
      ctx.clip(); // clip canvas to card size
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.opacity})`;
        ctx.fill();

        p.x += p.dx;
        p.y += p.dy;

        if (p.x < 0 || p.x > width) p.dx *= -1;
        if (p.y < 0 || p.y > height) p.dy *= -1;
      }
      ctx.restore();
    };

    let anim: number;
    const loop = () => {
      draw();
      anim = requestAnimationFrame(loop);
    };

    init();
    loop();

    const resizeObserver = new ResizeObserver(() => {
      width = container.clientWidth;
      height = container.clientHeight;
      canvas.width = width;
      canvas.height = height;
      init();
    });

    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(anim);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("absolute inset-0 pointer-events-none", className)}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      {children ? (
        <motion.div
          className={cn("absolute inset-0 z-10", className)}
          animate={
            animation
              ? { scale: [1, 1.05, 1], opacity: [0.9, 1, 0.9] }
              : undefined
          }
          transition={
            animation
              ? { repeat: Infinity, duration: 4, ease: "easeInOut" }
              : undefined
          }
        >
          {children}
        </motion.div>
      ) : null}
    </div>
  );
};
